/**
 * Exame toxicológico do motorista — integração com a API.
 *
 * `registrarColeta` e `lancarLaudo` são chamadas distintas de propósito: no
 * servidor têm permissões diferentes, porque registrar a coleta é rotina de
 * quem atende e lançar o laudo é ato de quem recebeu o documento oficial.
 */

import apiClient from './apiClient';
import type {
  ToxicologicalExam,
  DadosDaColeta,
  DadosDoLaudo,
  PreviaEsocial,
} from '../types/toxicologicalExam';

export * from '../types/toxicologicalExam';

const BASE = '/exames-toxicologicos';

/**
 * O `apiClient` tipa o corpo como possivelmente ausente. Em vez de espalhar `?.`
 * por todo método — que devolveria `undefined` silencioso para a tela —, falha
 * aqui, onde dá para dizer o que estava sendo buscado.
 */
function corpo<T>(recebido: { data?: T } | undefined, oQue: string): T {
  const valor = recebido?.data;
  if (valor === undefined || valor === null) {
    throw new Error(`Resposta vazia do servidor ao buscar ${oQue}`);
  }
  return valor;
}

interface RespostaLista {
  message: string;
  data: ToxicologicalExam[];
  meta?: { mesesEntreExames: number };
}

interface RespostaItem {
  message: string;
  data: ToxicologicalExam;
}

interface RespostaPrevia {
  message: string;
  data: PreviaEsocial;
}

export const toxicologicalExamService = {
  async listar(): Promise<ToxicologicalExam[]> {
    const { data } = await apiClient.get<RespostaLista>(BASE);
    return corpo(data, 'os exames toxicológicos');
  },

  async listarPorAtendimento(appointmentId: number): Promise<ToxicologicalExam[]> {
    const { data } = await apiClient.get<RespostaLista>(`${BASE}/atendimento/${appointmentId}`);
    return corpo(data, 'os exames do atendimento');
  },

  async historicoDoFuncionario(employeeId: number): Promise<ToxicologicalExam[]> {
    const { data } = await apiClient.get<RespostaLista>(`${BASE}/funcionario/${employeeId}`);
    return corpo(data, 'o histórico do funcionário');
  },

  async buscarPorId(id: number): Promise<ToxicologicalExam> {
    const { data } = await apiClient.get<RespostaItem>(`${BASE}/${id}`);
    return corpo(data, 'o exame toxicológico');
  },

  async registrarColeta(dados: DadosDaColeta): Promise<ToxicologicalExam> {
    const { data } = await apiClient.post<RespostaItem>(BASE, dados);
    return corpo(data, 'a coleta registrada');
  },

  async lancarLaudo(id: number, dados: DadosDoLaudo): Promise<ToxicologicalExam> {
    const { data } = await apiClient.patch<RespostaItem>(`${BASE}/${id}`, dados);
    return corpo(data, 'o laudo lançado');
  },

  async excluir(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  /**
   * O que seria enviado ao eSocial e o que ainda falta.
   *
   * A tela mostra isso ANTES de transmitir — o governo rejeita sem dizer qual
   * campo faltou, e descobrir pelo retorno dele custa dias.
   */
  async previaEsocial(id: number): Promise<PreviaEsocial> {
    const { data } = await apiClient.get<RespostaPrevia>(`${BASE}/${id}/previa-esocial`);
    return corpo(data, 'a prévia do eSocial');
  },
};

export default toxicologicalExamService;
